import { Address, GenderCode, LightUser, ProfessionCode, TeamRole, User, UserCompany } from 'pleinchamp-api-client';
import { plcDayjs } from '@utils/date';
import { Dayjs } from 'dayjs';

export type ProfileFormState = {
  genderCode: GenderCode;
  lastname?: string;
  firstname?: string;
  birthday?: Dayjs;
  address?: string;
  additionalAddress?: string;
  zipPostcode?: string;
  city?: string;
  phoneNumber?: string;
  email?: string;
  username?: string;
  enterpriseName?: string;
  enterpriseAddress?: string;
  enterpriseAdditionalAddress?: string;
  enterpriseZipPostcode?: string;
  enterpriseCity?: string;
  professions: ProfessionCode[];
};

export const ADDRESS_MAX_LENGTH = 300;
export const ZIPCODE_MAX_LENGTH = 5;
export const PHONE_MAX_LENGTH = 10;

export function getRequiredFields(user?: User): (keyof ProfileFormState)[] {
  return user?.teamRole === TeamRole.Admin
    ? ['genderCode', 'lastname', 'firstname', 'email', 'username', 'zipPostcode']
    : ['lastname', 'firstname', 'email', 'username', 'zipPostcode'];
}

export function initForm(user: User | undefined): ProfileFormState {
  return {
    additionalAddress: user?.address?.line2,
    address: user?.address?.line1,
    birthday: user?.birthday ? plcDayjs(user.birthday) : undefined,
    city: user?.address?.city,
    email: user?.email,
    enterpriseAdditionalAddress: user?.company?.address?.line2,
    enterpriseAddress: user?.company?.address?.line1,
    enterpriseCity: user?.company?.address?.city,
    enterpriseName: user?.company?.name,
    enterpriseZipPostcode: user?.company?.address?.zipPostcode,
    firstname: user?.firstname,
    genderCode: user?.genderCode ? user.genderCode : GenderCode.Undefined,
    lastname: user?.lastname,
    phoneNumber: user?.phoneNumber,
    professions: user?.professions ? user.professions : [],
    username: user?.username,
    zipPostcode: user?.address?.zipPostcode,
  };
}

export function convertFormToUserEntity(formData: ProfileFormState): LightUser | undefined {
  const {
    firstname,
    lastname,
    genderCode,
    birthday,
    additionalAddress,
    address: line1,
    email,
    username,
    phoneNumber,
    enterpriseAdditionalAddress,
    enterpriseAddress,
    enterpriseCity,
    enterpriseName,
    enterpriseZipPostcode,
    zipPostcode,
    city,
    professions,
  } = formData;

  if (!email || !username) {
    return undefined;
  }

  const address: Address | undefined =
    line1 || city || zipPostcode
      ? {
          city: String(city),
          country: 'France',
          countryCode: 'FR',
          line1: String(line1),
          line2: additionalAddress,
          zipPostcode: String(zipPostcode),
          stateOrProvince: '',
          stateOrProvinceCode: '',
        }
      : undefined;
  const entrepriseAddress: Address | undefined =
    enterpriseAddress && enterpriseCity
      ? {
          city: enterpriseCity,
          country: 'France',
          countryCode: 'FR',
          line1: enterpriseAddress,
          line2: enterpriseAdditionalAddress,
          zipPostcode: String(enterpriseZipPostcode),
          stateOrProvince: '',
          stateOrProvinceCode: '',
        }
      : undefined;

  const company: UserCompany | undefined = enterpriseName
    ? { address: entrepriseAddress, name: enterpriseName }
    : undefined;
  return {
    address,
    birthday: birthday ? birthday.toDate() : undefined,
    company,
    email,
    firstname,
    genderCode,
    lastname,
    phoneNumber,
    professions,
    username,
  };
}
